import { Trans, useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import { PauseChip } from '../components/pause-chip';
import { CheckCircleIcon, CONTAINER_CLASS, EYEBROW_CLASS, GOLD_EM_CLASS, SECTION_CLASS, SECTION_HEAD_CLASS, SECTION_LEAD_CLASS, SECTION_TITLE_CLASS, SectionBackground, SimChip } from '../components/ui';
import { IMAGE_BASE, V4A_NAMESPACE, VIDEO_BASE } from '../constants';
import { useDocumentVisible, useReducedMotion } from '../hooks/use-page-motion';

type Side = 'bad' | 'good';

const CLIP: Record<Side, string> = { bad: 'ba-sem-noping', good: 'ba-com-noping' };

/* Two masters (1280 and 640 wide) in WebM (VP9) and MP4 (H.264): the size by the box width x DPR, the
   format by what the browser plays (WebM first, it is the lighter file). */
function pickSource(video: HTMLVideoElement, side: Side): string {
  const width = video.getBoundingClientRect().width || 0;
  const small = width > 0 && width * (window.devicePixelRatio || 1) <= 640;
  const webm = typeof video.canPlayType === 'function' && video.canPlayType('video/webm; codecs="vp9"') !== '';
  return `${VIDEO_BASE}/${CLIP[side]}-${small ? 640 : 1280}.${webm ? 'webm' : 'mp4'}`;
}

interface ClipProps {
  side: Side;
  label: string;
  playing: boolean;
}

/**
 * The file is attached only once the panel is near the screen (preload none; the poster paints in every
 * state), and it is fetched once as a whole 0.2 to 1 MB blob, so the browser never aborts and re-requests
 * a byte range. It plays only while on screen, the tab is visible and nobody paused the band.
 */
function Clip({ side, label, playing }: ClipProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const shouldPlay = useRef(false);
  shouldPlay.current = playing && inView;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    if (typeof IntersectionObserver !== 'function') {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => setInView(entry.isIntersecting)), { rootMargin: '200px 0px', threshold: 0 });
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (inView && playing) setLoaded(true);
  }, [inView, playing]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !loaded) return undefined;
    const source = pickSource(video, side);
    let objectUrl: string | null = null;
    let cancelled = false;
    const play = () => {
      if (shouldPlay.current) video.play()?.catch(() => undefined);
    };
    video.addEventListener('canplay', play, { once: true });
    fetch(source)
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        video.src = objectUrl;
      })
      .catch(() => {
        if (!cancelled) video.src = source;
      });
    return () => {
      cancelled = true;
      video.removeEventListener('canplay', play);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [loaded, side]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !loaded) return;
    if (playing && inView) video.play()?.catch(() => undefined);
    else if (!video.paused) video.pause();
  }, [playing, inView, loaded]);

  return <video ref={videoRef} className='ba__video size-full object-cover' width={1280} height={544} muted loop playsInline preload='none' poster={`${IMAGE_BASE}/${CLIP[side]}-poster.webp`} aria-label={label} />;
}

const STAT_CELL_CLASS = 'min-w-0 rounded-[var(--r-sm)] border border-solid border-[color:var(--rim-2)] bg-[color:rgb(0_0_0_/_.28)] px-[.55rem] py-2';
const STAT_TERM_CLASS = 'min-h-[2.5em] font-mono text-[length:11px] uppercase leading-[1.25] tracking-[.06em] text-[color:var(--fg-faint-aa)]';
const STAT_VALUE_CLASS = 'tabular m-0 mt-[.18rem] whitespace-nowrap text-[length:clamp(1rem,_.85rem_+_.5vw,_1.25rem)] font-bold tabular-nums';
const UNIT_CLASS = 'text-[length:var(--fs-xs)] font-semibold text-[color:var(--fg-dim)]';

interface PanelStats {
  ping: string;
  loss: string;
  fps: string;
}

const STATS: Record<Side, PanelStats> = { bad: { ping: '208', loss: '16,0', fps: '86' }, good: { ping: '42', loss: '0,0', fps: '180' } };

function Stats({ side }: { side: Side }) {
  const { t } = useTranslation(V4A_NAMESPACE);
  const stats = STATS[side];
  const tone = side === 'bad' ? 'text-[color:var(--curse-dim)]' : 'text-[color:var(--accent)]';
  const loss = side === 'bad' ? t('before_after.loss_bad') : t('before_after.loss_good');
  return (
    <dl className='ba__stats m-0 grid min-w-0 grid-cols-3 gap-2'>
      <div className={STAT_CELL_CLASS}>
        <dt className={STAT_TERM_CLASS}>{t('before_after.stat_ping')}</dt>
        <dd className={`${STAT_VALUE_CLASS} ${tone}`}>
          {stats.ping} <span className={UNIT_CLASS}>ms</span>
        </dd>
      </div>
      <div className={STAT_CELL_CLASS}>
        <dt className={STAT_TERM_CLASS}>{t('before_after.stat_loss')}</dt>
        <dd className={`${STAT_VALUE_CLASS} ${tone}`}>
          {loss}
          <span className={UNIT_CLASS}>%</span>
        </dd>
      </div>
      <div className={STAT_CELL_CLASS}>
        <dt className={STAT_TERM_CLASS}>FPS</dt>
        <dd className={`${STAT_VALUE_CLASS} ${tone}`}>{stats.fps}</dd>
      </div>
    </dl>
  );
}

/** "Os amaldiçoados e os encantados": the same match with and without NoPing, side by side. */
export function BeforeAfter() {
  const { t } = useTranslation(V4A_NAMESPACE);
  const reduced = useReducedMotion();
  const pageVisible = useDocumentVisible();
  const [paused, setPaused] = useState<boolean | null>(null);
  const isPaused = paused ?? reduced;
  const bandRef = useRef<HTMLDivElement>(null);

  // One control owns every moving thing in the band, the blinking "travou" chip included (WCAG 2.2.2).
  useEffect(() => {
    bandRef.current?.classList.toggle('is-paused', isPaused);
  }, [isPaused]);

  const playing = !isPaused && pageVisible;

  return (
    <section className={`${SECTION_CLASS} sec--ba`} id='maldicao' aria-labelledby='ba-title'>
      <SectionBackground halos={[{ position: 'left', tone: 'violet' }]} />
      <div className={CONTAINER_CLASS}>
        <div className={`${SECTION_HEAD_CLASS} mb-[clamp(2rem,_1rem_+_3vw,_3.5rem)]`} data-reveal=''>
          <div className='min-w-0'>
            <p className={EYEBROW_CLASS}>{t('before_after.eyebrow')}</p>
            <h2 className={SECTION_TITLE_CLASS} id='ba-title'>
              <Trans t={t} i18nKey='before_after.title' components={{ em: <em className={GOLD_EM_CLASS} /> }} />
            </h2>
          </div>
          <p className={`${SECTION_LEAD_CLASS} m-0`}>{t('before_after.lead')}</p>
        </div>

        <div ref={bandRef} className='ba grid items-stretch gap-4 min-[992px]:grid-cols-[minmax(0,_1fr)_auto_minmax(0,_1fr)] min-[992px]:items-center min-[992px]:gap-[clamp(1rem,_.3rem_+_1.4vw,_1.75rem)]' data-reveal=''>
          <article className='ba__panel ba__panel--bad grid min-w-0 gap-3 rounded-[var(--r-lg)] border border-solid border-[color:rgb(var(--curse-rgb)_/_.28)] p-[clamp(.85rem,_.6rem_+_.9vw,_1.15rem)] [box-shadow:var(--shadow-md)] forced-colors:border-[color:CanvasText]'>
            <header className='ba__top flex min-w-0 flex-wrap items-center justify-between gap-2'>
              <span className='ba__tag bg-[color:rgb(var(--curse-rgb)_/_.18)] px-2 py-1 font-mono text-[length:var(--fs-2xs)] uppercase tracking-[var(--ls-eyebrow)] text-[color:#FFD9E0]'>{t('before_after.without')}</span>
              <span className='ba__what min-w-0 text-[length:var(--fs-xs)] text-[color:var(--fg-dim)]'>{t('before_after.without_route')}</span>
            </header>
            <div className='ba__screen relative aspect-[1280/544] min-w-0 overflow-hidden rounded-[var(--r-md)] bg-[color:#05040A]'>
              <Clip side='bad' label={t('before_after.video_bad')} playing={playing} />
              <span className='ba__hud ba__hud--bad absolute bottom-[.6rem] start-[.6rem] inline-flex items-baseline gap-[.2rem] rounded-[var(--r-sm)] border border-solid border-[color:rgb(var(--curse-rgb)_/_.55)] bg-[color:rgb(var(--curse-rgb)_/_.3)] px-[.55rem] py-[.3rem] font-bold leading-none text-[color:#FFE3E8]'>
                <b className='tabular text-[length:clamp(1.1rem,_.9rem_+_.8vw,_1.5rem)] tabular-nums'>208</b>
                <em className='text-[length:var(--fs-xs)] font-semibold not-italic'>ms</em>
              </span>
              <span className='ba__spike absolute bottom-[.6rem] end-[.6rem] rounded-[var(--r-sm)] border border-solid border-[color:rgb(var(--curse-rgb)_/_.6)] bg-[color:rgb(var(--curse-rgb)_/_.32)] px-[.45rem] py-[.22rem] font-mono text-[length:11px] uppercase tracking-[.1em] text-[color:#FFE3E8]' aria-hidden='true'>
                {t('before_after.spike')}
              </span>
            </div>
            <Stats side='bad' />
            <p className='ba__note m-0 flex min-w-0 items-start gap-[.45rem] text-[length:var(--fs-xs)] leading-[1.45] text-[color:var(--fg-dim)]'>
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
                <path d='M12 8.5v5' />
                <path d='M12 16.6h.01' />
                <circle cx='12' cy='12' r='9' />
              </svg>
              {t('before_after.note_bad')}
            </p>
          </article>

          <div className='ba__mid flex min-w-0 flex-none items-center justify-center gap-3 min-[992px]:flex-col' aria-hidden='true'>
            <span className='ba__delta grid justify-items-center gap-[.1rem]'>
              <b className='whitespace-nowrap text-[length:clamp(1.1rem,_.9rem_+_.7vw,_1.4rem)] font-bold text-[color:var(--accent)]'>−80%</b>
              <small className='font-mono text-[length:11px] uppercase tracking-[.1em] text-[color:var(--fg-faint-aa)]'>{t('before_after.delta_ping')}</small>
            </span>
            <span className='ba__arrow inline-flex size-12 items-center justify-center rounded-[50%] border border-solid border-[color:rgb(var(--accent-rgb)_/_.4)] bg-[color:rgb(var(--accent-rgb)_/_.1)] text-[color:var(--accent)] [box-shadow:0_0_24px_rgb(var(--accent-rgb)_/_.22)]'>
              <svg width='26' height='26' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' focusable='false'>
                <path d='M4 12h14' />
                <path d='m13 6 6 6-6 6' />
              </svg>
            </span>
            <span className='ba__delta ba__delta--fps grid justify-items-center gap-[.1rem]'>
              <b className='whitespace-nowrap text-[length:clamp(1.1rem,_.9rem_+_.7vw,_1.4rem)] font-bold text-[color:var(--gold)]'>+110%</b>
              <small className='font-mono text-[length:11px] uppercase tracking-[.1em] text-[color:var(--fg-faint-aa)]'>FPS</small>
            </span>
          </div>

          <article className='ba__panel ba__panel--good grid min-w-0 gap-3 rounded-[var(--r-lg)] border border-solid border-[color:rgb(var(--accent-rgb)_/_.3)] p-[clamp(.85rem,_.6rem_+_.9vw,_1.15rem)] [box-shadow:var(--shadow-md),_0_0_40px_rgb(var(--accent-rgb)_/_.08)] forced-colors:border-[color:CanvasText]'>
            <header className='ba__top flex min-w-0 flex-wrap items-center justify-between gap-2'>
              <span className='ba__tag rounded-[var(--r-sm)] bg-[color:var(--accent)] px-2 py-1 font-mono text-[length:var(--fs-2xs)] uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--accent-ink)] forced-colors:border forced-colors:border-solid forced-colors:border-[color:CanvasText]'>{t('before_after.with')}</span>
              <span className='ba__what min-w-0 text-[length:var(--fs-xs)] text-[color:var(--fg-dim)]'>{t('before_after.with_route')}</span>
            </header>
            <div className='ba__screen relative aspect-[1280/544] min-w-0 overflow-hidden rounded-[var(--r-md)] bg-[color:#05040A]'>
              <Clip side='good' label={t('before_after.video_good')} playing={playing} />
              <span className='ba__hud ba__hud--good absolute bottom-[.6rem] start-[.6rem] inline-flex items-baseline gap-[.2rem] rounded-[var(--r-sm)] border border-solid border-[color:rgb(var(--accent-rgb)_/_.5)] bg-[color:rgb(8_6_15_/_.72)] px-[.55rem] py-[.3rem] font-bold leading-none text-[color:var(--accent)]'>
                <b className='tabular text-[length:clamp(1.1rem,_.9rem_+_.8vw,_1.5rem)] tabular-nums'>42</b>
                <em className='text-[length:var(--fs-xs)] font-semibold not-italic'>ms</em>
              </span>
            </div>
            <Stats side='good' />
            <p className='ba__note ba__note--ok m-0 flex min-w-0 items-start gap-[.45rem] text-[length:var(--fs-xs)] leading-[1.45] text-[color:var(--fg-dim)]'>
              <CheckCircleIcon strokeWidth='1.9' size={16} />
              {t('before_after.note_good')}
            </p>
          </article>
        </div>

        <div className='ba__bar mt-5 flex flex-wrap items-center justify-between gap-4 border-t border-solid border-t-[color:var(--rim)] pt-4' data-reveal=''>
          <p className='ba__cap m-0 flex min-w-0 flex-[1_1_18rem] flex-wrap items-center gap-2 text-[length:var(--fs-xs)] leading-normal text-[color:var(--fg-dim)]'>
            <SimChip label={t('sim.label')} /> {t('before_after.caption')}
          </p>
          <PauseChip paused={isPaused} onToggle={() => setPaused(!isPaused)} labelPause={t('before_after.pause')} labelResume={t('before_after.resume')} resumeText={t('controls.play')} iconSize={16} />
        </div>
      </div>
    </section>
  );
}
